import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { Modal } from '../components/Modal'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import {
  ETAPA_LABELS,
  etapaPorStatus,
  podeValidarEtapa,
  temDivergencia,
  validationBadgeClass,
  type EtapaValidacao,
} from '../lib/entregaValidacao'
import { kgParaUnidadesComerciais } from '../lib/produtoConversaoKg'
import { supabase } from '../lib/supabase'
import {
  downloadText,
  formatDate,
  formatNumber,
  monthBoundsISO,
  toCsv,
} from '../lib/utils'
import type {
  AlimentoMacronutriente,
  Entrega,
  EntregaItem,
  PlanejamentoEscola,
  PlanejamentoItem,
  PlanejamentoRota,
  Produto,
  Rota,
  StatusPlanejamento,
  StatusValidacaoEntrega,
} from '../types/database'
import { GRUPOS, STATUS_VALIDACAO_LABELS } from '../types/database'

type PlanoRow = PlanejamentoRota & {
  compra?: { id: string; codigo?: string; status_validacao?: string } | null
  rota?: {
    nome: string
    nutricionista_id?: string | null
    nutricionista?: { nome: string; email: string } | null
  }
  escolas?: (PlanejamentoEscola & {
    escola?: { nome: string }
    itens?: (PlanejamentoItem & { alimento?: { nome: string; categoria?: string; produto_id?: string | null } })[]
  })[]
}

type EntregaRow = Entrega & {
  escola?: { nome: string }
  rota?: { nome: string; nutricionista_id?: string | null }
  entrega_itens?: (EntregaItem & { alimento?: { nome: string; categoria?: string; produto_id?: string | null } })[]
  validado_fornecedor?: { nome: string } | null
  validado_escola?: { nome: string } | null
  validado_nutricionista?: { nome: string } | null
}

type DraftItem = {
  key: string
  alimento_id: string
  produtoNome: string
  categoria: string
  genero: string
  qtd_planejada: number
  /** Quantidade informada pela escola (snapshot para a nutricionista). */
  qtd_escola: number
  qtd_recebida: string
}

function formatQtdProdutoUn(
  qtdKg: number | string,
  produto: Produto | null | undefined,
): string {
  const kg = Number(qtdKg)
  if (!produto || !Number.isFinite(kg) || kg < 0) return '—'
  if (kg === 0) return formatNumber(0, 3)
  const un = kgParaUnidadesComerciais(kg, produto)
  return un != null ? formatNumber(un, 3) : '—'
}

function produtoDoAlimento(
  alimento: { produto_id?: string | null } | undefined,
  produtoById: Map<string, Produto>,
): Produto | undefined {
  if (!alimento?.produto_id) return undefined
  return produtoById.get(alimento.produto_id)
}

type AnexoView = {
  id: string
  nome_arquivo: string
  url: string
}

type EscolaResponsavelOpt = {
  usuario_id: string
  nome: string
  email: string
}

type ValidateSchoolState = {
  planejamento_escola_id: string
  escola_id: string
  nome: string
  ordem: number
  entregaId?: string
  status_validacao: StatusValidacaoEntrega | null
  fornecedor_nome: string
  recebido_por: string
  justificativa: string
  itens: DraftItem[]
  validado_fornecedor_em: string | null
  validado_fornecedor_nome: string | null
  validado_escola_em: string | null
  validado_escola_nome: string | null
  validado_nutricionista_em: string | null
  validado_nutricionista_nome: string | null
}

type ModalMode = 'validar' | 'ver'
type Tab = 'planejamentos' | 'historico'

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusBadgeClass(status: StatusPlanejamento) {
  switch (status) {
    case 'concluido':
      return 'badge badge-ok'
    case 'parcial':
      return 'badge badge-atencao'
    case 'cancelado':
      return 'badge badge-neutral'
    default:
      return 'badge badge-zerado'
  }
}

function statusLabel(status: StatusPlanejamento) {
  switch (status) {
    case 'planejado':
      return 'Planejado'
    case 'parcial':
      return 'Parcial'
    case 'concluido':
      return 'Concluído'
    case 'cancelado':
      return 'Cancelado'
  }
}

function stepDone(status: StatusValidacaoEntrega | null, step: EtapaValidacao): boolean {
  if (!status || status === 'pendente_fornecedor') return false
  if (status === 'concluida') return true
  if (step === 'fornecedor') return true
  if (step === 'escola') return status === 'pendente_nutricionista'
  return false
}

function stepCurrent(status: StatusValidacaoEntrega | null, step: EtapaValidacao): boolean {
  return etapaPorStatus(status ?? 'pendente_fornecedor') === step
}

export function EntregasPage() {
  const { canWrite, canDelete, profile } = useAuth()
  const [tab, setTab] = useState<Tab>('planejamentos')
  const [planos, setPlanos] = useState<PlanoRow[]>([])
  const [entregas, setEntregas] = useState<EntregaRow[]>([])
  const [entregasPorParada, setEntregasPorParada] = useState<Record<string, EntregaRow>>({})
  const [rotas, setRotas] = useState<Rota[]>([])
  const [alimentos, setAlimentos] = useState<AlimentoMacronutriente[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [myEscolaIds, setMyEscolaIds] = useState<Set<string>>(new Set())
  const [myRotaIds, setMyRotaIds] = useState<Set<string>>(new Set())
  const [responsaveisPorEscola, setResponsaveisPorEscola] = useState<
    Record<string, EscolaResponsavelOpt[]>
  >({})
  const [filtroRota, setFiltroRota] = useState('')
  const [filtroInicio, setFiltroInicio] = useState(() => monthBoundsISO().inicio)
  const [filtroFim, setFiltroFim] = useState(() => monthBoundsISO().fim)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<ModalMode>('validar')
  const [activePlano, setActivePlano] = useState<PlanoRow | null>(null)
  const [schools, setSchools] = useState<ValidateSchoolState[]>([])
  const [activeSchoolId, setActiveSchoolId] = useState<string | null>(null)
  const [fotoFiles, setFotoFiles] = useState<File[]>([])
  const [anexos, setAnexos] = useState<AnexoView[]>([])
  const [saving, setSaving] = useState(false)
  const writable = canWrite('entregas')
  const planoSort = useTableSort<PlanoRow>('data_entrega', 'desc')
  const histSort = useTableSort<EntregaRow>('data', 'desc')

  const produtoById = useMemo(() => new Map(produtos.map((p) => [p.id, p])), [produtos])
  const hasLoadedRef = useRef(false)

  const load = useCallback(async () => {
    if (!profile) return
    const isInitialLoad = !hasLoadedRef.current
    if (isInitialLoad) setLoading(true)
    setError(null)

    const scopeQueries: Array<Promise<void>> = []
    let escolaScope = new Set<string>()
    let rotaScope = new Set<string>()

    if (profile.perfil === 'responsavel_escola') {
      scopeQueries.push(
        (async () => {
          const { data } = await supabase
            .from('escola_responsaveis')
            .select('escola_id')
            .eq('usuario_id', profile.id)
          escolaScope = new Set(((data || []) as { escola_id: string }[]).map((r) => r.escola_id))
        })(),
      )
    }
    if (profile.perfil === 'nutricionista') {
      scopeQueries.push(
        (async () => {
          const { data } = await supabase.from('rotas').select('id').eq('nutricionista_id', profile.id)
          rotaScope = new Set(((data || []) as { id: string }[]).map((r) => r.id))
        })(),
      )
    }
    await Promise.all(scopeQueries)
    setMyEscolaIds(escolaScope)
    setMyRotaIds(rotaScope)

    const [p, e, r, alim, pr, resp] = await Promise.all([
      supabase
        .from('planejamento_rotas')
        .select(
          '*, compra:compras(id, codigo, status_validacao), rota:rotas(nome, nutricionista_id, nutricionista:usuarios!rotas_nutricionista_id_fkey(nome, email)), responsavel_entrega:usuarios!planejamento_rotas_responsavel_entrega_id_fkey(nome, email), escolas:planejamento_escolas(*, escola:escolas(nome), itens:planejamento_itens(*, alimento:alimentos_macronutrientes(nome, categoria, produto_id)))',
        )
        .neq('status', 'cancelado')
        .order('data_entrega', { ascending: false })
        .limit(300),
      supabase
        .from('entregas')
        .select(
          '*, escola:escolas(nome), rota:rotas(nome, nutricionista_id), entrega_itens(*, alimento:alimentos_macronutrientes(nome, categoria, produto_id)), validado_fornecedor:usuarios!entregas_validado_fornecedor_por_fkey(nome), validado_escola:usuarios!entregas_validado_escola_por_fkey(nome), validado_nutricionista:usuarios!entregas_validado_nutricionista_por_fkey(nome)',
        )
        .order('data', { ascending: false })
        .limit(500),
      supabase.from('rotas').select('*, nutricionista:usuarios!rotas_nutricionista_id_fkey(nome, email)').order('nome'),
      supabase.from('alimentos_macronutrientes').select('*').order('nome'),
      supabase.from('produtos').select('*').order('nome'),
      supabase
        .from('escola_responsaveis')
        .select('escola_id, usuario_id, usuario:usuarios(nome, email)'),
    ])

    if (p.error || e.error || r.error || alim.error || pr.error || resp.error) {
      setError(
        p.error?.message ||
          e.error?.message ||
          r.error?.message ||
          alim.error?.message ||
          pr.error?.message ||
          resp.error?.message ||
          'Erro',
      )
    } else {
      // Só planejamentos cujo pedido foi validado pela Nutricionista ISEN
      const rows = ((p.data as PlanoRow[]) || [])
        .filter((row) => row.compra?.status_validacao === 'validado_isen')
        .map((row) => ({
          ...row,
          escolas: [...(row.escolas || [])].sort((a, b) => a.ordem - b.ordem),
        }))
      setPlanos(rows)
      setEntregas((e.data as EntregaRow[]) || [])
      setRotas((r.data as Rota[]) || [])
      setAlimentos((alim.data as AlimentoMacronutriente[]) || [])
      setProdutos((pr.data as Produto[]) || [])
      const map: Record<string, EntregaRow> = {}
      for (const row of (e.data || []) as EntregaRow[]) {
        if (row.planejamento_escola_id) map[row.planejamento_escola_id] = row
      }
      setEntregasPorParada(map)
      const respMap: Record<string, EscolaResponsavelOpt[]> = {}
      for (const row of (resp.data || []) as unknown as {
        escola_id: string
        usuario_id: string
        usuario?: { nome: string; email: string } | { nome: string; email: string }[] | null
      }[]) {
        const usuario = Array.isArray(row.usuario) ? row.usuario[0] : row.usuario
        const list = respMap[row.escola_id] || []
        list.push({
          usuario_id: row.usuario_id,
          nome: usuario?.nome || row.usuario_id,
          email: usuario?.email || '',
        })
        respMap[row.escola_id] = list
      }
      setResponsaveisPorEscola(respMap)
    }
    hasLoadedRef.current = true
    if (isInitialLoad) setLoading(false)
  }, [profile])

  useEffect(() => {
    document.title = 'Entregas · ISEN EDUCAE'
    load()
  }, [load])

  const perfilLimitado =
    profile?.perfil === 'responsavel_escola' || profile?.perfil === 'nutricionista'

  const filteredPlanos = useMemo(() => {
    return planos.filter((p) => {
      if (filtroRota && p.rota_id !== filtroRota) return false
      if (filtroInicio && p.data_entrega < filtroInicio) return false
      if (filtroFim && p.data_entrega > filtroFim) return false
      if (profile?.perfil === 'responsavel_escola') {
        return (p.escolas || []).some((esc) => myEscolaIds.has(esc.escola_id))
      }
      if (profile?.perfil === 'nutricionista') {
        return myRotaIds.has(p.rota_id)
      }
      if (profile?.perfil === 'logistica') {
        return p.responsavel_entrega_id === profile.id
      }
      return true
    })
  }, [planos, filtroRota, filtroInicio, filtroFim, profile, myEscolaIds, myRotaIds])

  const planosSorted = useMemo(() => {
    const accessors: Record<string, (p: PlanoRow) => string | number> = {
      data_entrega: (p) => p.data_entrega,
      rota: (p) => p.rota?.nome || '',
      status: (p) => p.status,
      escolas: (p) => p.escolas?.length || 0,
    }
    return planoSort.sortRows(filteredPlanos, accessors)
  }, [filteredPlanos, planoSort])

  const myPlanejamentoEscolaIds = useMemo(() => {
    if (profile?.perfil !== 'logistica') return new Set<string>()
    const ids = new Set<string>()
    for (const p of planos) {
      if (p.responsavel_entrega_id !== profile.id) continue
      for (const esc of p.escolas || []) ids.add(esc.id)
    }
    return ids
  }, [planos, profile])

  const filteredHistorico = useMemo(() => {
    return entregas.filter((e) => {
      if (filtroRota && e.rota_id !== filtroRota) return false
      if (filtroInicio && e.data < filtroInicio) return false
      if (filtroFim && e.data > filtroFim) return false
      if (profile?.perfil === 'responsavel_escola') return myEscolaIds.has(e.escola_id)
      if (profile?.perfil === 'nutricionista') return myRotaIds.has(e.rota_id)
      if (profile?.perfil === 'logistica') {
        return !!e.planejamento_escola_id && myPlanejamentoEscolaIds.has(e.planejamento_escola_id)
      }
      return true
    })
  }, [
    entregas,
    filtroRota,
    filtroInicio,
    filtroFim,
    profile,
    myEscolaIds,
    myRotaIds,
    myPlanejamentoEscolaIds,
  ])

  const historicoSorted = useMemo(() => {
    const accessors: Record<string, (e: EntregaRow) => string | number> = {
      data: (e) => e.data,
      rota: (e) => e.rota?.nome || '',
      escola: (e) => e.escola?.nome || '',
      status: (e) => e.status_validacao || '',
      recebido_por: (e) => e.recebido_por,
    }
    return histSort.sortRows(filteredHistorico, accessors)
  }, [filteredHistorico, histSort])

  function generoLabel(categoria: string | undefined) {
    if (!categoria) return '—'
    return GRUPOS.find((g) => g.value === categoria)?.label || categoria
  }

  function buildItemsForSchool(
    esc: NonNullable<PlanoRow['escolas']>[number],
    entrega: EntregaRow | undefined,
  ): DraftItem[] {
    const planned = esc.itens || []
    const delivered = entrega?.entrega_itens || []
    const byProduct = new Map<string, DraftItem>()

    for (const it of planned) {
      const alimento =
        it.alimento || alimentos.find((a) => a.id === it.alimento_id)
      const categoria = alimento?.categoria || '—'
      const produto = produtoDoAlimento(alimento, produtoById)
      const qtdEntrega = Number(
        delivered.find((d) => d.alimento_id === it.alimento_id)?.quantidade ?? it.quantidade,
      )
      const qtdPlan = Number(
        delivered.find((d) => d.alimento_id === it.alimento_id)?.quantidade_planejada ??
          it.quantidade,
      )
      byProduct.set(it.alimento_id, {
        key: crypto.randomUUID(),
        alimento_id: it.alimento_id,
        produtoNome: produto?.nome || alimento?.nome || it.alimento_id,
        categoria,
        genero: generoLabel(categoria === '—' ? undefined : categoria),
        qtd_planejada: qtdPlan,
        qtd_escola: qtdEntrega,
        qtd_recebida: String(qtdEntrega),
      })
    }

    for (const it of delivered) {
      if (byProduct.has(it.alimento_id)) continue
      const alimento =
        it.alimento || alimentos.find((a) => a.id === it.alimento_id)
      const categoria = alimento?.categoria || '—'
      const produto = produtoDoAlimento(alimento, produtoById)
      const qtd = Number(it.quantidade)
      byProduct.set(it.alimento_id, {
        key: crypto.randomUUID(),
        alimento_id: it.alimento_id,
        produtoNome: produto?.nome || alimento?.nome || it.alimento_id,
        categoria,
        genero: generoLabel(categoria === '—' ? undefined : categoria),
        qtd_planejada: Number(it.quantidade_planejada ?? it.quantidade),
        qtd_escola: qtd,
        qtd_recebida: String(qtd),
      })
    }

    return [...byProduct.values()]
  }

  function defaultRecebidoPor(escolaId: string, entregaRecebido?: string | null) {
    if (entregaRecebido && entregaRecebido !== 'Pendente') return entregaRecebido
    const lista = responsaveisPorEscola[escolaId] || []
    if (profile?.perfil === 'responsavel_escola') {
      const eu = lista.find((r) => r.usuario_id === profile.id)
      if (eu) return eu.nome
    }
    if (lista.length === 1) return lista[0].nome
    return ''
  }

  function openPlanoModal(plano: PlanoRow, mode: ModalMode) {
    setError(null)
    setModalMode(mode)
    setActivePlano(plano)
    setActiveSchoolId(null)
    setFotoFiles([])
    setAnexos([])
    setSchools(
      (plano.escolas || [])
        .filter((esc) => {
          if (profile?.perfil === 'responsavel_escola') return myEscolaIds.has(esc.escola_id)
          return true
        })
        .map((esc) => {
          const entrega = entregasPorParada[esc.id] || entregas.find((e) => e.planejamento_escola_id === esc.id)
          const logisticaNome = plano.responsavel_entrega?.nome || ''
          return {
            planejamento_escola_id: esc.id,
            escola_id: esc.escola_id,
            nome: esc.escola?.nome || '—',
            ordem: esc.ordem,
            entregaId: entrega?.id,
            status_validacao: entrega?.status_validacao ?? null,
            fornecedor_nome: entrega?.fornecedor_nome || logisticaNome,
            recebido_por: defaultRecebidoPor(esc.escola_id, entrega?.recebido_por),
            justificativa: entrega?.justificativa_divergencia || '',
            itens: buildItemsForSchool(esc, entrega),
            validado_fornecedor_em: entrega?.validado_fornecedor_em ?? null,
            validado_fornecedor_nome: entrega?.validado_fornecedor?.nome ?? null,
            validado_escola_em: entrega?.validado_escola_em ?? null,
            validado_escola_nome: entrega?.validado_escola?.nome ?? null,
            validado_nutricionista_em: entrega?.validado_nutricionista_em ?? null,
            validado_nutricionista_nome: entrega?.validado_nutricionista?.nome ?? null,
          }
        }),
    )
    setModalOpen(true)
  }

  function updateSchool(id: string, patch: Partial<ValidateSchoolState>) {
    setSchools((prev) => prev.map((s) => (s.planejamento_escola_id === id ? { ...s, ...patch } : s)))
  }

  function updateItemRecebido(schoolId: string, itemKey: string, qtd_recebida: string) {
    setSchools((prev) =>
      prev.map((s) =>
        s.planejamento_escola_id !== schoolId
          ? s
          : {
              ...s,
              itens: s.itens.map((i) => (i.key === itemKey ? { ...i, qtd_recebida } : i)),
            },
      ),
    )
  }

  async function refreshPlanoStatus(planejamentoId: string, escolaIds: string[]) {
    const { data, error: err } = await supabase
      .from('entregas')
      .select('planejamento_escola_id, status_validacao')
      .in('planejamento_escola_id', escolaIds)
    if (err) {
      setError(err.message)
      return
    }

    const done = new Set(
      ((data || []) as { planejamento_escola_id: string | null; status_validacao: StatusValidacaoEntrega }[])
        .filter((r) => r.status_validacao === 'concluida')
        .map((r) => r.planejamento_escola_id)
        .filter(Boolean) as string[],
    )
    const total = escolaIds.length
    const validated = escolaIds.filter((id) => done.has(id)).length
    const status: StatusPlanejamento =
      validated === 0 ? 'planejado' : validated >= total ? 'concluido' : 'parcial'

    await supabase.from('planejamento_rotas').update({ status }).eq('id', planejamentoId)
  }

  async function loadAnexos(entregaId: string) {
    const { data, error: err } = await supabase
      .from('entrega_anexos')
      .select('id, path, nome_arquivo')
      .eq('entrega_id', entregaId)
      .order('criado_em', { ascending: true })
    if (err) {
      setAnexos([])
      return
    }
    const rows = (data || []) as { id: string; path: string; nome_arquivo: string }[]
    const views: AnexoView[] = []
    for (const row of rows) {
      const { data: signed } = await supabase.storage
        .from('entregas-comprovantes')
        .createSignedUrl(row.path, 3600)
      views.push({
        id: row.id,
        nome_arquivo: row.nome_arquivo,
        url: signed?.signedUrl || '',
      })
    }
    setAnexos(views)
  }

  async function uploadAnexos(entregaId: string, files: File[]) {
    if (!profile || !files.length) return
    for (const file of files) {
      const extMatch = file.name.match(/(\.[a-zA-Z0-9]+)$/)
      const ext = (extMatch?.[1] || '').toLowerCase() || '.bin'
      // Storage não aceita espaços/acentos na key — nome original fica em nome_arquivo
      const path = `${entregaId}/${crypto.randomUUID()}${ext}`
      const { error: upErr } = await supabase.storage.from('entregas-comprovantes').upload(path, file)
      if (upErr) throw upErr
      const { error: insErr } = await supabase.from('entrega_anexos').insert({
        entrega_id: entregaId,
        path,
        nome_arquivo: file.name,
        uploaded_by: profile.id,
      })
      if (insErr) throw insErr
    }
  }

  async function submitFornecedor(s: ValidateSchoolState) {
    if (!profile || !activePlano) return
    if (!activePlano.responsavel_entrega_id) {
      setError('Este planejamento não tem responsável de logística. Defina em Planejamento de rota.')
      return
    }
    if (
      profile.perfil === 'logistica' &&
      activePlano.responsavel_entrega_id !== profile.id
    ) {
      setError('Somente o responsável de logística atribuído a este planejamento pode validar.')
      return
    }
    const logisticaNome = activePlano.responsavel_entrega?.nome || s.fornecedor_nome.trim()
    if (!logisticaNome) {
      setError('Responsável de logística sem nome cadastrado')
      return
    }
    const itens = s.itens
      .map((i) => ({
        alimento_id: i.alimento_id,
        quantidade_planejada: i.qtd_planejada,
        quantidade: Number(i.qtd_recebida),
      }))
      .filter((i) => i.quantidade > 0)
    if (!itens.length) {
      setError('Informe ao menos um item entregue pela logística')
      return
    }

    setSaving(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('validar_entrega_fornecedor', {
      p_planejamento_escola_id: s.planejamento_escola_id,
      p_fornecedor_nome: logisticaNome,
      p_itens: itens,
    })
    setSaving(false)
    if (err) {
      setError(err.message)
      return
    }
    void data
    await refreshPlanoStatus(activePlano.id, (activePlano.escolas || []).map((e) => e.id))
    setActiveSchoolId(null)
    load()
    setModalOpen(false)
    setActivePlano(null)
  }

  async function submitEscola(s: ValidateSchoolState) {
    if (!profile || !s.entregaId) return
    if (!s.recebido_por.trim()) {
      setError('Selecione o responsável da escola / CEACT')
      return
    }
    const responsaveis = responsaveisPorEscola[s.escola_id] || []
    if (!responsaveis.some((r) => r.nome === s.recebido_por.trim())) {
      setError('Selecione um responsável vinculado a esta escola')
      return
    }
    if (temDivergencia(s.itens) && !s.justificativa.trim()) {
      setError('Justifique a divergência entre planejado e recebido')
      return
    }

    const itens = s.itens.map((i) => ({
      alimento_id: i.alimento_id,
      quantidade_planejada: i.qtd_planejada,
      quantidade: Number(i.qtd_recebida),
    }))

    setSaving(true)
    setError(null)
    try {
      await uploadAnexos(s.entregaId, fotoFiles)
    } catch (uploadErr) {
      setSaving(false)
      setError(uploadErr instanceof Error ? uploadErr.message : 'Erro ao enviar foto')
      return
    }
    const { error: err } = await supabase.rpc('validar_entrega_escola', {
      p_entrega_id: s.entregaId,
      p_recebido_por: s.recebido_por.trim(),
      p_justificativa: s.justificativa.trim() || null,
      p_itens: itens,
    })
    if (err) {
      setSaving(false)
      setError(err.message)
      return
    }
    setSaving(false)
    setFotoFiles([])
    if (activePlano) {
      await refreshPlanoStatus(activePlano.id, (activePlano.escolas || []).map((e) => e.id))
    }
    setActiveSchoolId(null)
    load()
    setModalOpen(false)
    setActivePlano(null)
  }

  async function submitNutricionista(s: ValidateSchoolState) {
    if (!s.entregaId || !activePlano) return
    if (!activePlano.rota?.nutricionista_id) {
      setError('Esta rota não tem nutricionista vinculada. Defina em Configurações → Rotas.')
      return
    }
    if (
      profile?.perfil === 'nutricionista' &&
      activePlano.rota.nutricionista_id !== profile.id
    ) {
      setError('Somente a nutricionista responsável por esta rota pode validar.')
      return
    }
    setSaving(true)
    setError(null)
    const itens = s.itens.map((i) => ({
      alimento_id: i.alimento_id,
      quantidade: Number(i.qtd_recebida),
    }))
    if (!itens.some((i) => i.quantidade > 0)) {
      setSaving(false)
      setError('Informe ao menos um item com quantidade final maior que zero')
      return
    }
    const { error: err } = await supabase.rpc('validar_entrega_nutricionista', {
      p_entrega_id: s.entregaId,
      p_itens: itens,
    })
    setSaving(false)
    if (err) {
      setError(err.message)
      return
    }
    await refreshPlanoStatus(activePlano.id, (activePlano.escolas || []).map((e) => e.id))
    setActiveSchoolId(null)
    setAnexos([])
    load()
    setModalOpen(false)
    setActivePlano(null)
  }

  async function removeEntrega(id: string) {
    if (
      !confirm(
        'Excluir esta entrega? As entradas de estoque geradas por ela também serão removidas.',
      )
    ) {
      return
    }
    const target = entregas.find((e) => e.id === id)
    const { error: err } = await supabase.from('entregas').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return
    }
    if (target?.planejamento_escola_id) {
      const plano = planos.find((p) =>
        p.escolas?.some((e) => e.id === target.planejamento_escola_id),
      )
      if (plano) {
        await refreshPlanoStatus(
          plano.id,
          (plano.escolas || []).map((e) => e.id),
        )
      }
    }
    load()
  }

  function exportCsv() {
    if (tab === 'planejamentos') {
      downloadText(
        'entregas-planejamentos.csv',
        toCsv(
          filteredPlanos.map((p) => ({
            Data: p.data_entrega,
            Rota: p.rota?.nome,
            Status: statusLabel(p.status),
            Escolas: p.escolas?.length || 0,
            Concluidas: (p.escolas || []).filter(
              (e) => entregasPorParada[e.id]?.status_validacao === 'concluida',
            ).length,
          })),
        ),
      )
      return
    }
    downloadText(
      'entregas-historico.csv',
      toCsv(
        filteredHistorico.map((e) => ({
          Data: e.data,
          Escola: e.escola?.nome,
          Rota: e.rota?.nome,
          Validação: STATUS_VALIDACAO_LABELS[e.status_validacao] || e.status_validacao,
          Logística: e.fornecedor_nome,
          'Recebido por': e.recebido_por,
          Justificativa: e.justificativa_divergencia,
        })),
      ),
    )
  }

  function renderValidationForm(s: ValidateSchoolState) {
    const status = s.status_validacao ?? 'pendente_fornecedor'
    const etapa = etapaPorStatus(status)
    const minhasEscolas = myEscolaIds.has(s.escola_id)
    const minhaRota = activePlano ? myRotaIds.has(activePlano.rota_id) : false
    const meuPlanejamentoLogistica =
      !!activePlano?.responsavel_entrega_id &&
      (profile?.perfil === 'admin' || activePlano.responsavel_entrega_id === profile?.id)
    const canAct =
      modalMode === 'validar' &&
      etapa &&
      podeValidarEtapa(profile?.perfil, etapa, {
        minhasEscolas,
        minhaRota,
        meuPlanejamentoLogistica,
      })

    if (!canAct || activeSchoolId !== s.planejamento_escola_id) return null

    if (etapa === 'fornecedor') {
      const logisticaNome =
        activePlano?.responsavel_entrega?.nome || s.fornecedor_nome || '—'
      return (
        <div className="validation-step-form">
          <div className="field">
            <label>Responsável logística (atribuído ao planejamento)</label>
            <input value={logisticaNome} readOnly disabled />
          </div>
          {!activePlano?.responsavel_entrega_id && (
            <div className="alert alert-error" style={{ marginTop: 8 }}>
              Defina o responsável pela entrega em Planejamento de rota antes de validar.
            </div>
          )}
          <p className="kpi-sub">
            Confirme as quantidades saídas com a logística (padrão = planejado).
          </p>
          {renderItensTable(s, true)}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ marginTop: 12 }}
            disabled={saving || !activePlano?.responsavel_entrega_id}
            onClick={() => void submitFornecedor(s)}
          >
            {saving ? 'Salvando…' : 'Validar logística'}
          </button>
        </div>
      )
    }

    if (etapa === 'escola') {
      const divergente = temDivergencia(s.itens)
      const responsaveis = responsaveisPorEscola[s.escola_id] || []
      return (
        <div className="validation-step-form">
          <div className="field">
            <label>Recebido por (responsável da escola / CEACT) *</label>
            {responsaveis.length ? (
              <select
                value={s.recebido_por}
                onChange={(e) =>
                  updateSchool(s.planejamento_escola_id, { recebido_por: e.target.value })
                }
              >
                <option value="">Selecione o responsável…</option>
                {responsaveis.map((r) => (
                  <option key={r.usuario_id} value={r.nome}>
                    {r.nome}
                    {r.email ? ` — ${r.email}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input value="" disabled placeholder="Nenhum responsável vinculado" />
                <p className="kpi-sub" style={{ marginTop: 6 }}>
                  Vincule responsáveis em Configurações → Escolas.
                </p>
              </>
            )}
          </div>
          {renderItensTable(s, true)}
          {divergente && (
            <>
              <div className="field" style={{ marginTop: 8 }}>
                <label>Justificativa da divergência *</label>
                <textarea
                  className="menu-desc-field"
                  value={s.justificativa}
                  onChange={(e) => updateSchool(s.planejamento_escola_id, { justificativa: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Foto comprovante</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  multiple
                  onChange={(e) => setFotoFiles(Array.from(e.target.files || []))}
                />
              </div>
            </>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ marginTop: 12 }}
            disabled={saving || !responsaveis.length}
            onClick={() => void submitEscola(s)}
          >
            {saving ? 'Salvando…' : 'Validar recebimento da escola'}
          </button>
        </div>
      )
    }

    if (etapa === 'nutricionista') {
      const nutriNome =
        activePlano?.rota?.nutricionista?.nome ||
        rotas.find((r) => r.id === activePlano?.rota_id)?.nutricionista?.nome ||
        '—'
      const temNutri = !!activePlano?.rota?.nutricionista_id
      return (
        <div className="validation-step-form">
          <div className="field">
            <label>Nutricionista responsável pela rota</label>
            <input value={nutriNome} readOnly disabled />
          </div>
          {!temNutri && (
            <div className="alert alert-error" style={{ marginTop: 8 }}>
              Vincule a nutricionista em Configurações → Rotas antes de validar.
            </div>
          )}
          <p className="kpi-sub">
            Confira o relato da escola e defina a quantidade final que entrará no estoque.
          </p>
          {s.justificativa && (
            <div className="field" style={{ marginTop: 8 }}>
              <label>Justificativa da escola</label>
              <textarea className="menu-desc-field" value={s.justificativa} readOnly disabled />
            </div>
          )}
          {anexos.length > 0 && (
            <div className="field" style={{ marginTop: 8 }}>
              <label>Fotos anexadas pela escola</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {anexos.map((a) =>
                  a.url ? (
                    <a
                      key={a.id}
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'block' }}
                    >
                      <img
                        src={a.url}
                        alt={a.nome_arquivo}
                        style={{
                          width: 96,
                          height: 96,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: '1px solid var(--color-border)',
                        }}
                      />
                    </a>
                  ) : (
                    <span key={a.id} className="kpi-sub">
                      {a.nome_arquivo}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
          {renderItensTable(s, true, 'nutri')}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ marginTop: 12 }}
            disabled={saving || !temNutri}
            onClick={() => void submitNutricionista(s)}
          >
            {saving ? 'Salvando…' : 'Confirmar e registrar no estoque'}
          </button>
        </div>
      )
    }

    return null
  }

  function renderItensTable(
    s: ValidateSchoolState,
    editable: boolean,
    modo: 'padrao' | 'nutri' = 'padrao',
  ) {
    if (!editable && modo === 'padrao') {
      const entregaConcluida = s.status_validacao === 'concluida'
      return (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th className="col-compact">Categoria</th>
                <th>Produto</th>
                <th className="col-num">Qnt produto pedido (un)</th>
                <th className="col-num">Qnt produto entregue</th>
              </tr>
            </thead>
            <tbody>
              {s.itens.map((row) => {
                const alimento = alimentos.find((a) => a.id === row.alimento_id)
                const produto = produtoDoAlimento(alimento, produtoById)
                return (
                  <tr key={row.key}>
                    <td className="col-compact">{row.categoria}</td>
                    <td>{row.produtoNome}</td>
                    <td className="col-num">{formatQtdProdutoUn(row.qtd_planejada, produto)}</td>
                    <td className="col-num">
                      {entregaConcluida
                        ? formatQtdProdutoUn(row.qtd_recebida, produto)
                        : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }

    return (
      <div className="table-wrap" style={{ marginTop: 8 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th className="col-compact">Gênero</th>
              <th>Produto</th>
              <th className="col-num">Planejado</th>
              {modo === 'nutri' && <th className="col-num">Recebido (escola)</th>}
              <th className="col-num">
                {modo === 'nutri' ? 'Qtd. final (estoque)' : editable ? 'Recebido' : 'Entregue'}
              </th>
            </tr>
          </thead>
          <tbody>
            {s.itens.map((row) => (
              <tr key={row.key}>
                <td className="col-compact">{row.genero}</td>
                <td>{row.produtoNome}</td>
                <td className="col-num">{formatNumber(row.qtd_planejada, 3)}</td>
                {modo === 'nutri' && (
                  <td className="col-num">{formatNumber(row.qtd_escola, 3)}</td>
                )}
                <td className="col-num">
                  {editable ? (
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={row.qtd_recebida}
                      onChange={(e) =>
                        updateItemRecebido(s.planejamento_escola_id, row.key, e.target.value)
                      }
                    />
                  ) : (
                    formatNumber(Number(row.qtd_recebida) || 0, 3)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (loading) return <div className="loading">Carregando entregas…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Entregas</h1>
        <div className="page-actions">
          {!perfilLimitado && (
            <button type="button" className="btn btn-secondary" onClick={exportCsv}>
              Exportar CSV
            </button>
          )}
          {!perfilLimitado && (
            <Link to="/planejamento-rota" className="btn btn-secondary">
              Ir para Planejamento
            </Link>
          )}
        </div>
      </div>
      {error && !modalOpen && <div className="alert alert-error">{error}</div>}

      <p className="kpi-sub" style={{ marginBottom: 12 }}>
        Só aparecem planejamentos cujo pedido foi <strong>validado pela Nutricionista ISEN</strong>.
        Pedidos em rascunho ou aguardando validação ficam só em Pedido / Planejamento de rota.
      </p>

      <div className="tabs" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`tab ${tab === 'planejamentos' ? 'active' : ''}`}
          onClick={() => setTab('planejamentos')}
        >
          Planejamentos
        </button>
        <button
          type="button"
          className={`tab ${tab === 'historico' ? 'active' : ''}`}
          onClick={() => setTab('historico')}
        >
          Histórico
        </button>
      </div>

      <div className="filters">
        <div className="field">
          <label>Início</label>
          <input type="date" value={filtroInicio} onChange={(e) => setFiltroInicio(e.target.value)} />
        </div>
        <div className="field">
          <label>Fim</label>
          <input type="date" value={filtroFim} onChange={(e) => setFiltroFim(e.target.value)} />
        </div>
        <div className="field">
          <label>Rota</label>
          <select value={filtroRota} onChange={(e) => setFiltroRota(e.target.value)}>
            <option value="">Todas</option>
            {rotas.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {tab === 'planejamentos' ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Data" sortKey="data_entrega" activeKey={planoSort.sortKey} direction={planoSort.sortDir} onSort={planoSort.toggleSort} col="date" />
                <SortableTh label="Rota" sortKey="rota" activeKey={planoSort.sortKey} direction={planoSort.sortDir} onSort={planoSort.toggleSort} col="text" />
                <SortableTh label="Status" sortKey="status" activeKey={planoSort.sortKey} direction={planoSort.sortDir} onSort={planoSort.toggleSort} col="status" />
                <SortableTh label="Escolas" sortKey="escolas" activeKey={planoSort.sortKey} direction={planoSort.sortDir} onSort={planoSort.toggleSort} col="num" />
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {planosSorted.map((p) => {
                const concluidas = (p.escolas || []).filter(
                  (e) => entregasPorParada[e.id]?.status_validacao === 'concluida',
                ).length
                const total = p.escolas?.length || 0
                return (
                  <tr key={p.id}>
                    <td className="col-date">{formatDate(p.data_entrega)}</td>
                    <td className="col-text">{p.rota?.nome || '—'}</td>
                    <td className="col-status">
                      <span className={statusBadgeClass(p.status)}>{statusLabel(p.status)}</span>
                    </td>
                    <td className="col-num">
                      {concluidas}/{total}
                    </td>
                    <td className="col-actions">
                      <div className="table-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openPlanoModal(p, 'ver')}>
                          Ver
                        </button>
                        {writable && p.status !== 'concluido' && (
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => openPlanoModal(p, 'validar')}>
                            Validar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
              {!planosSorted.length && (
                <tr>
                  <td colSpan={5} className="kpi-sub">
                    {perfilLimitado
                      ? 'Nenhuma entrega pendente para você.'
                      : (
                        <>
                          Nenhum planejamento. Crie em{' '}
                          <Link to="/planejamento-rota">Planejamento de rota</Link>.
                        </>
                      )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Data" sortKey="data" activeKey={histSort.sortKey} direction={histSort.sortDir} onSort={histSort.toggleSort} col="date" />
                <SortableTh label="Rota" sortKey="rota" activeKey={histSort.sortKey} direction={histSort.sortDir} onSort={histSort.toggleSort} col="text" />
                <SortableTh label="Escola" sortKey="escola" activeKey={histSort.sortKey} direction={histSort.sortDir} onSort={histSort.toggleSort} col="name" />
                <SortableTh label="Validação" sortKey="status" activeKey={histSort.sortKey} direction={histSort.sortDir} onSort={histSort.toggleSort} col="status" />
                <SortableTh label="Recebido por" sortKey="recebido_por" activeKey={histSort.sortKey} direction={histSort.sortDir} onSort={histSort.toggleSort} col="text" />
                {canDelete('entregas') && <th className="col-actions" />}
              </tr>
            </thead>
            <tbody>
              {historicoSorted.map((e) => (
                <tr key={e.id}>
                  <td className="col-date">{formatDate(e.data)}</td>
                  <td className="col-text">{e.rota?.nome}</td>
                  <td className="col-name">{e.escola?.nome}</td>
                  <td className="col-status">
                    <span className="badge badge-neutral">
                      {STATUS_VALIDACAO_LABELS[e.status_validacao] || e.status_validacao}
                    </span>
                  </td>
                  <td className="col-text">{e.recebido_por}</td>
                  {canDelete('entregas') && (
                    <td className="col-actions">
                      <button type="button" className="btn btn-ghost" onClick={() => removeEntrega(e.id)}>
                        Excluir
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modalOpen}
        size="xl"
        title={
          activePlano
            ? `${modalMode === 'ver' ? 'Detalhe' : 'Validar'} · ${activePlano.rota?.nome || 'Rota'} · ${formatDate(activePlano.data_entrega)}`
            : 'Entrega'
        }
        onClose={() => {
          setModalOpen(false)
          setActivePlano(null)
          setActiveSchoolId(null)
          setFotoFiles([])
        }}
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setModalOpen(false)
              setActivePlano(null)
              setActiveSchoolId(null)
            }}
          >
            Fechar
          </button>
        }
      >
        {error && modalOpen && <div className="alert alert-error">{error}</div>}
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Três validações: logística → escola/CEACT → nutricionista da rota. A logística é o
          responsável atribuído no planejamento. Estoque só entra após a validação da nutricionista.
        </p>
        {activePlano?.responsavel_entrega?.nome && (
          <p className="kpi-sub" style={{ marginBottom: 12 }}>
            Responsável logística deste planejamento: <strong>{activePlano.responsavel_entrega.nome}</strong>
          </p>
        )}
        {activePlano?.rota?.nutricionista?.nome && (
          <p className="kpi-sub" style={{ marginBottom: 12 }}>
            Nutricionista da rota: <strong>{activePlano.rota.nutricionista.nome}</strong>
          </p>
        )}
        {schools.map((s) => {
          const status = s.status_validacao ?? 'pendente_fornecedor'
          const etapa = etapaPorStatus(status)
          const minhasEscolas = myEscolaIds.has(s.escola_id)
          const minhaRota = activePlano ? myRotaIds.has(activePlano.rota_id) : false
          const meuPlanejamentoLogistica =
            !!activePlano?.responsavel_entrega_id &&
            (profile?.perfil === 'admin' || activePlano.responsavel_entrega_id === profile?.id)
          const canAct =
            modalMode === 'validar' &&
            etapa &&
            podeValidarEtapa(profile?.perfil, etapa, {
              minhasEscolas,
              minhaRota,
              meuPlanejamentoLogistica,
            })

          return (
            <div key={s.planejamento_escola_id} className="card" style={{ marginBottom: 12, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <strong>
                  {s.ordem}. {s.nome}
                </strong>
                <span className="badge badge-neutral">
                  {STATUS_VALIDACAO_LABELS[status] || status}
                </span>
              </div>
              <div className="validation-steps">
                {(['fornecedor', 'escola', 'nutricionista'] as EtapaValidacao[]).map((step) => (
                  <span
                    key={step}
                    className={validationBadgeClass(stepDone(status, step), stepCurrent(status, step))}
                  >
                    {ETAPA_LABELS[step]}
                    {stepDone(status, step) ? ' ✓' : ''}
                  </span>
                ))}
              </div>
              {(s.validado_fornecedor_em || s.validado_escola_em || s.validado_nutricionista_em) && (
                <div className="kpi-sub" style={{ marginTop: 8, display: 'grid', gap: 4 }}>
                  {s.validado_fornecedor_em && (
                    <div>
                      Validado logística:{' '}
                      <strong>{s.validado_fornecedor_nome || '—'}</strong>
                      {' · '}
                      {formatDateTime(s.validado_fornecedor_em)}
                    </div>
                  )}
                  {s.validado_escola_em && (
                    <div>
                      Validado escola:{' '}
                      <strong>{s.validado_escola_nome || '—'}</strong>
                      {' · '}
                      {formatDateTime(s.validado_escola_em)}
                    </div>
                  )}
                  {s.validado_nutricionista_em && (
                    <div>
                      Validado nutricionista:{' '}
                      <strong>{s.validado_nutricionista_nome || '—'}</strong>
                      {' · '}
                      {formatDateTime(s.validado_nutricionista_em)}
                    </div>
                  )}
                </div>
              )}
              {(modalMode === 'ver' || status === 'concluida') && renderItensTable(s, false)}
              {canAct && activeSchoolId !== s.planejamento_escola_id && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ marginTop: 12 }}
                  onClick={() => {
                    setActiveSchoolId(s.planejamento_escola_id)
                    setFotoFiles([])
                    setAnexos([])
                    setError(null)
                    if (s.entregaId) void loadAnexos(s.entregaId)
                  }}
                >
                  {ETAPA_LABELS[etapa!]}
                </button>
              )}
              {renderValidationForm(s)}
            </div>
          )
        })}
      </Modal>
    </div>
  )
}
